import React, { useState, useMemo } from 'react';
import './InventoryTable.css';
import { InventoryData, Store, Product } from '../types/inventory';
import logoImage from '../images/logo.webp';

interface InventoryTableProps {
    data: InventoryData;
}

export default function InventoryTable({ data }: InventoryTableProps) {
    const [selectedStore, setSelectedStore] = useState<string>('all');
    const [selectedBrand, setSelectedBrand] = useState<string>('all');
    const [selectedSubcategory, setSelectedSubcategory] = useState<string>('all');
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ 
        key: 'current_price', 
        direction: 'asc' 
    });

    // Combine all products and add store information
    const allProducts = useMemo(() => {
        return data.stores.flatMap(store => 
            store.products.map(product => ({
                ...product,
                storeName: store.name,
                storeLocation: store.location
            }))
        );
    }, [data.stores]);

    // Get unique brands and subcategories across all stores
    const brands = useMemo(() => {
        const brandSet = new Set(allProducts.map(product => product.brand));
        return ['all', ...Array.from(brandSet)];
    }, [allProducts]);

    const subcategories = useMemo(() => {
        const subcategorySet = new Set(allProducts.map(product => product.subcategory));
        return ['all', ...Array.from(subcategorySet)];
    }, [allProducts]);

    // Filter products
    const filteredProducts = useMemo(() => {
        return allProducts.filter(product => {
            const matchesStore = selectedStore === 'all' || product.storeName === selectedStore;
            const matchesBrand = selectedBrand === 'all' || product.brand === selectedBrand;
            const matchesSubcategory = selectedSubcategory === 'all' || product.subcategory === selectedSubcategory;
            return matchesStore && matchesBrand && matchesSubcategory;
        });
    }, [allProducts, selectedStore, selectedBrand, selectedSubcategory]);

    // Sort products
    const sortedProducts = useMemo(() => {
        return [...filteredProducts].sort((a, b) => {
            if (a[sortConfig.key as keyof typeof a] < b[sortConfig.key as keyof typeof a]) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (a[sortConfig.key as keyof typeof a] > b[sortConfig.key as keyof typeof a]) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredProducts, sortConfig]);

    const requestSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Calculate total products and latest update
    const totalProducts = allProducts.length;
    const latestUpdate = new Date(Math.max(...data.stores.map(store => new Date(store.scraped_at).getTime()))).toLocaleString();

    return (
        <div className="inventory-table">
            <div className="header-section">
                <img src={logoImage} alt="Wizard of Ounces Logo" className="logo" />
            </div>

            <div className="store-info">
                <p className="store-stat">Total Products: {totalProducts}</p>
                <p className="store-stat">Number of Stores: {data.stores.length}</p>
            </div>

            <div className="filters">
                <div className="filter-group">
                    <label htmlFor="store-filter">Store:</label>
                    <select
                        id="store-filter"
                        value={selectedStore}
                        onChange={(e) => setSelectedStore(e.target.value)}
                    >
                        <option value="all">All Stores</option>
                        {data.stores.map(store => (
                            <option key={store.name} value={store.name}>
                                {store.name} - {store.location}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="brand-filter">Brand:</label>
                    <select
                        id="brand-filter"
                        value={selectedBrand}
                        onChange={(e) => setSelectedBrand(e.target.value)}
                    >
                        {brands.map(brand => (
                            <option key={brand} value={brand}>
                                {brand === 'all' ? 'All Brands' : brand}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="subcategory-filter">Category:</label>
                    <select
                        id="subcategory-filter"
                        value={selectedSubcategory}
                        onChange={(e) => setSelectedSubcategory(e.target.value)}
                    >
                        {subcategories.map(subcategory => (
                            <option key={subcategory} value={subcategory}>
                                {subcategory === 'all' ? 'All Categories' : subcategory}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <table className="table">
                <thead>
                    <tr>
                        <th className="sortable" onClick={() => requestSort('storeName')}>Store</th>
                        <th className="sortable" onClick={() => requestSort('brand')}>Brand</th>
                        <th className="sortable" onClick={() => requestSort('name')}>Product Name</th>
                        <th className="sortable" onClick={() => requestSort('subcategory')}>Category</th>
                        <th className="sortable" onClick={() => requestSort('original_price')}>Original Price</th>
                        <th>Discount</th>
                        <th className="sortable" onClick={() => requestSort('current_price')}>Current Price</th>
                        <th>View</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedProducts.map((product, index) => (
                        <tr key={index}>
                            <td>{product.storeName} - {product.storeLocation}</td>
                            <td>{product.brand}</td>
                            <td>{product.name}</td>
                            <td>{product.subcategory}</td>
                            <td>${product.original_price.toFixed(2)}</td>
                            <td>{product.discount ? product.discount.replace(/\.\d+%/, '%') : '-'}</td>
                            <td>${product.current_price.toFixed(2)}</td>
                            <td>
                                <a href={product.product_link} target="_blank" rel="noopener noreferrer" className="cart-button">
                                    🛒
                                </a>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
} 